<template>
  <b-modal
    :visible="visible"
    size="lg"
    hide-footer
    scrollable
    :title="$t('pages.productAppointments.upcoming.modalTitle')"
    @hide="$emit('close')"
  >
    <div class="product-client-summary">
      <span class="product-client-summary__icon">
        <feather-icon icon="UserIcon" size="22" />
      </span>
      <div>
        <div class="font-weight-bolder">{{ clientName }}</div>
        <small v-if="client && client.phoneNumber" class="text-muted">
          {{ client.phoneNumber }}
        </small>
        <small v-if="client && client.email" class="text-muted d-block">
          {{ client.email }}
        </small>
      </div>
    </div>

    <div class="d-flex align-items-center justify-content-between mb-1">
      <div>
        <h5 class="mb-25">
          {{ $t("pages.productAppointments.upcoming.nextAppointments") }}
        </h5>
        <small class="text-muted">
          {{
            $t("pages.productAppointments.upcoming.rangeDescription", {
              start: formatDate(rangeStart),
              end: formatDate(rangeEnd),
            })
          }}
        </small>
      </div>
      <b-badge pill variant="light-primary">{{ appointments.length }}</b-badge>
    </div>

    <div v-if="loading" class="text-center py-4">
      <b-spinner variant="primary" class="mb-1" />
      <p class="text-muted mb-0">
        {{ $t("pages.productAppointments.upcoming.loading") }}
      </p>
    </div>

    <b-alert
      v-else-if="!appointments.length"
      show
      variant="light-info"
      class="text-center mb-0"
    >
      <feather-icon icon="CalendarIcon" size="28" class="mb-50" />
      <p class="mb-0">
        {{ $t("pages.productAppointments.upcoming.empty") }}
      </p>
    </b-alert>

    <div v-else class="product-appointment-list">
      <article
        v-for="appointment in appointments"
        :key="appointment.idAppointment"
        class="product-appointment-item"
      >
        <div class="product-appointment-item__date">
          <strong>{{ formatDay(appointment.appointmentDate) }}</strong>
          <small>{{ formatMonth(appointment.appointmentDate) }}</small>
        </div>
        <div class="product-appointment-item__content">
          <div class="d-flex flex-wrap align-items-center mb-50">
            <h5 class="mb-0 mr-75">
              {{
                appointment.product && appointment.product.name
                  ? appointment.product.name
                  : $t("pages.productAppointments.upcoming.withoutProduct")
              }}
            </h5>
            <b-badge
              v-if="appointment.nameStatus"
              pill
              :variant="appointment.colorStatus || 'light-primary'"
            >
              {{ appointment.nameStatus }}
            </b-badge>
          </div>
          <div class="product-appointment-item__meta">
            <span>
              <feather-icon icon="ClockIcon" size="15" />
              {{ formatTimeRange(appointment) }}
            </span>
            <span>
              <feather-icon icon="UserCheckIcon" size="15" />
              {{
                appointment.employee && appointment.employee.name
                  ? appointment.employee.name
                  : $t("pages.productAppointments.upcoming.withoutEmployee")
              }}
            </span>
            <span>
              <feather-icon icon="UserIcon" size="15" />
              {{ appointmentClientNames(appointment) }}
            </span>
          </div>
        </div>
        <div class="product-appointment-item__actions">
          <b-button
            v-if="canEdit"
            size="sm"
            variant="flat-primary"
            :title="$t('pages.productAppointments.upcoming.editAction')"
            @click="$emit('edit', appointment)"
          >
            <feather-icon icon="Edit2Icon" size="17" />
          </b-button>
          <b-button
            v-if="canDelete"
            size="sm"
            variant="flat-danger"
            :title="$t('pages.productAppointments.upcoming.deleteAction')"
            @click="$emit('delete', appointment)"
          >
            <feather-icon icon="Trash2Icon" size="17" />
          </b-button>
        </div>
      </article>
    </div>
  </b-modal>
</template>

<script>
import {
  BAlert,
  BBadge,
  BButton,
  BModal,
  BSpinner,
} from "bootstrap-vue";
import moment from "moment";

export default {
  name: "ProductUpcomingAppointmentsModal",
  components: { BAlert, BBadge, BButton, BModal, BSpinner },
  props: {
    visible: { type: Boolean, default: false },
    client: { type: Object, default: null },
    appointments: { type: Array, default: () => [] },
    loading: { type: Boolean, default: false },
    rangeStart: { type: [String, Date], default: null },
    rangeEnd: { type: [String, Date], default: null },
    canEdit: { type: Boolean, default: false },
    canDelete: { type: Boolean, default: false },
  },
  computed: {
    clientName() {
      return (
        (this.client && this.client.label) ||
        this.$t("pages.productAppointments.upcoming.client")
      );
    },
  },
  methods: {
    formatDate(value) {
      return value ? moment(value).format("DD/MM/YYYY") : "";
    },
    formatDay(value) {
      return moment(value).format("DD");
    },
    formatMonth(value) {
      return moment(value).format("MMM");
    },
    formatTimeRange(appointment) {
      const start = moment(appointment.appointmentDate).format("hh:mm A");
      const end = appointment.endDateTime
        ? moment(appointment.endDateTime).format("hh:mm A")
        : "";
      return end ? `${start} - ${end}` : start;
    },
    appointmentClientNames(appointment) {
      const clients = Array.isArray(appointment.clients)
        ? appointment.clients
        : [];
      const names = clients
        .map((client) =>
          `${client.name || ""} ${client.lastName || ""}`.trim(),
        )
        .filter(Boolean);
      return names.length ? names.join(", ") : this.clientName;
    },
  },
};
</script>

<style lang="scss" scoped>
.product-client-summary {
  display: flex;
  align-items: center;
  gap: 0.85rem;
  padding: 1rem;
  margin-bottom: 1.25rem;
  border-radius: 0.65rem;
  background: rgba(115, 103, 240, 0.08);
}

.product-client-summary__icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 42px;
  height: 42px;
  border-radius: 50%;
  background: #7367f0;
  color: #fff;
}

.product-appointment-list {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.product-appointment-item {
  display: grid;
  grid-template-columns: 58px minmax(0, 1fr) auto;
  gap: 1rem;
  align-items: center;
  padding: 1rem;
  border: 1px solid #ebe9f1;
  border-radius: 0.65rem;
}

.product-appointment-item__date {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 58px;
  flex-direction: column;
  border-radius: 0.55rem;
  background: rgba(115, 103, 240, 0.1);
  color: #7367f0;
  text-transform: uppercase;
}

.product-appointment-item__date strong {
  font-size: 1.35rem;
}

.product-appointment-item__meta {
  display: flex;
  flex-wrap: wrap;
  gap: 0.45rem 1rem;
  color: #6e6b7b;
  font-size: 0.86rem;
}

.product-appointment-item__meta span {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
}

.product-appointment-item__actions {
  display: flex;
}

@media (max-width: 575.98px) {
  .product-appointment-item {
    grid-template-columns: 52px minmax(0, 1fr);
  }

  .product-appointment-item__actions {
    grid-column: 1 / -1;
    justify-content: flex-end;
    border-top: 1px solid #ebe9f1;
    padding-top: 0.5rem;
  }
}
</style>
